/**
 * CUST-EXP-001A · Canonical 8-group presentation mapping for the
 * Customer Command Centre. Every group derives deterministically from
 * the 10-phase canonical catalogue (see backend/core/task_catalogue.py):
 *
 *   sales_design           → Survey     (initial enquiry + site survey +
 *                                        design proposal + quote + contract)
 *   technical_design       → Design     (roof + electrical + layout + approval)
 *   permissions            → Permissions
 *   procurement            → Preparation (bundled: materials + install planning)
 *   installation_planning  → Preparation
 *   installation           → Installation
 *   commissioning          → Certification (bundled: energised + tested + certs)
 *   certification          → Certification
 *   customer_handover      → Handover
 *   aftercare              → Aftercare
 *
 * No new workflow state is created — the group's status and progress
 * are computed live from the phases the backend already returns.
 *
 * FIELD-001C Section C · When the dashboard payload lacks phases[]
 * (a genuine data hole rather than a completion signal), the current
 * phase caption is still authoritative for "which group is CURRENT".
 * We therefore look up the current group from the `current_phase`
 * label as a fallback so a Commissioning-stage project doesn't
 * render as eight pending steps. Earlier groups remain `not_started`
 * — we NEVER mark them complete just because a later phase started.
 */

export const JOURNEY_GROUPS = [
    { key: "survey",        label: "Survey",        phaseKeys: ["sales_design"] },
    { key: "design",        label: "Design",        phaseKeys: ["technical_design"] },
    { key: "permissions",   label: "Permissions",   phaseKeys: ["permissions"] },
    { key: "preparation",   label: "Preparation",   phaseKeys: ["procurement", "installation_planning"] },
    { key: "installation",  label: "Installation",  phaseKeys: ["installation"] },
    { key: "certification", label: "Certification", phaseKeys: ["commissioning", "certification"] },
    { key: "handover",      label: "Handover",      phaseKeys: ["customer_handover"] },
    { key: "aftercare",     label: "Aftercare",     phaseKeys: ["aftercare"] },
];

// Human-visible `current_phase` strings the backend emits, mapped
// to their presentation group. Kept lowercase so we can match
// case-insensitively without inventing new state.
const PHASE_LABEL_TO_GROUP = {
    "sales & design":        "survey",
    "site survey":           "survey",
    "design proposal":       "survey",
    "technical design":      "design",
    "permissions":           "permissions",
    "procurement":           "preparation",
    "installation planning": "preparation",
    "preparation":           "preparation",
    "installation":          "installation",
    "commissioning":         "certification",
    "certification":         "certification",
    "customer handover":     "handover",
    "handover":              "handover",
    "aftercare":             "aftercare",
};

const GROUP_ORDER = JOURNEY_GROUPS.map((g) => g.key);

/** Resolve which presentation group the backend's `current_phase`
 * caption points at. Never invents state — returns null when the
 * caption is missing, "Complete", or unrecognised.
 */
export function currentGroupFromPhaseLabel(project) {
    const raw = String(project?.current_phase || "").trim().toLowerCase();
    if (!raw) return null;
    if (raw === "complete" || raw === "complete (data mismatch)") return null;
    return PHASE_LABEL_TO_GROUP[raw] || null;
}

// Helpers that tolerate the two shapes we've seen in the wild —
// canonical `phase_key` / `status` (post-serialize) and the raw
// persisted docs that sometimes use `key` / `state`. Reading both
// removes the guesswork when a legacy row survives a migration.
function _phaseKey(p) {
    return p?.phase_key || p?.key || p?.slug || null;
}
function _phaseStatus(p) {
    return String(p?.status || p?.state || "").toLowerCase();
}

// Canonical phase_key order — matches `backend/core/task_catalogue.py`
// (`build_phases()` emits phases in this exact sequence). Used to
// recover a `phase_key` from array position when the persisted doc
// stored none. Sarah's live project row is the concrete case: 10
// phases in canonical order, with `phase_key: null` on every one.
const CANONICAL_PHASE_KEYS = [
    "sales_design", "technical_design", "permissions", "procurement",
    "installation_planning", "installation", "commissioning",
    "certification", "customer_handover", "aftercare",
];

/** Decorate every phase in `project.phases` with a resolved
 *  `phase_key`: prefer the explicit field, fall back to the legacy
 *  `key`/`slug`, and finally recover by ordinal position when the
 *  phase count matches the canonical catalogue. The recovery step
 *  restores rail parity for projects whose stored phase docs
 *  predate the `phase_key` migration.
 */
function _phasesWithResolvedKeys(project) {
    const phases = project?.phases;
    if (!Array.isArray(phases) || phases.length === 0) return [];
    return phases.map((p, idx) => {
        const explicit = _phaseKey(p);
        if (explicit) return p;
        // Only recover by position when the phase array is the
        // full canonical length — otherwise position is unsafe.
        if (phases.length === CANONICAL_PHASE_KEYS.length) {
            return { ...p, phase_key: CANONICAL_PHASE_KEYS[idx] };
        }
        return p;
    });
}

/** Compute the status of a presentation group from the canonical phases.
 *  Returns "complete" only when every mapped required task is done,
 *  "in_progress" when any mapped task is in-progress or partly complete,
 *  otherwise "not_started". Optional tasks are excluded from the
 *  denominator, matching backend `compute_phase_status` semantics.
 *
 *  When the payload gives phases but omits task-level data (dashboard
 *  slice), fall back to the backend-emitted `phase.status` field —
 *  the same field `/journey` uses to render its badges. When neither
 *  tasks nor phase.status is available for this group, use the
 *  `current_phase` caption only to surface the CURRENT group as
 *  in-progress. Earlier groups NEVER get rolled up to complete
 *  without positive evidence.
 */
export function computeGroupState(project, group) {
    const resolved = _phasesWithResolvedKeys(project);
    const phases = resolved.filter((p) => group.phaseKeys.includes(_phaseKey(p)));
    const requiredTasks = phases.flatMap((p) => (p.tasks || []).filter((t) => !t.optional));
    const allTasks = phases.flatMap((p) => p.tasks || []);
    if (allTasks.length > 0) {
        // Prefer required-only denominator; if every task is marked
        // optional (or the seed stored `optional: null`), fall back
        // to all tasks so a genuinely-complete phase still rolls up.
        const denom = requiredTasks.length > 0 ? requiredTasks : allTasks;
        const done = denom.filter((t) => t.status === "complete").length;
        const anyProgress = allTasks.some((t) => t.status === "in_progress")
            || allTasks.some((t) => t.status === "complete");
        let status;
        if (done === denom.length) status = "complete";
        else if (anyProgress) status = "in_progress";
        else status = "not_started";
        const percent = Math.round((done / denom.length) * 100);
        return { status, done, total: denom.length, percent };
    }
    // No task-level rollup — try phase-level status (the same field
    // `/journey` uses). Complete requires every mapped phase to be
    // complete; in-progress requires at least one to be in-progress
    // or complete.
    if (phases.length > 0) {
        const stats = phases.map(_phaseStatus);
        const allComplete = stats.length > 0 && stats.every((s) => s === "complete");
        const anyStarted = stats.some((s) => s === "in_progress" || s === "complete");
        if (allComplete) return { status: "complete", done: phases.length, total: phases.length, percent: 100 };
        if (anyStarted) {
            const doneCount = stats.filter((s) => s === "complete").length;
            const percent = Math.round((doneCount / phases.length) * 100);
            return { status: "in_progress", done: doneCount, total: phases.length, percent: Math.max(percent, 15) };
        }
        return { status: "not_started", done: 0, total: phases.length, percent: 0 };
    }
    // Final fallback: current-phase caption. Only the CURRENT group
    // gets in_progress; every other group stays not_started so we
    // never falsely claim completion.
    const currentGroup = currentGroupFromPhaseLabel(project);
    if (currentGroup === group.key) {
        return { status: "in_progress", done: 0, total: 0, percent: 25 };
    }
    return { status: "not_started", done: 0, total: 0, percent: 0 };
}

/** Returns the mapped presentation group key for the current project
 *  phase (or the first non-complete group). Used to visually highlight
 *  the currently-active group on the journey rail.
 */
export function activeGroupKey(project) {
    if (!project) return null;
    // If task-level data is present, honour it strictly.
    const hasPhases = Array.isArray(project.phases) && project.phases.length > 0;
    if (hasPhases) {
        for (const group of JOURNEY_GROUPS) {
            const s = computeGroupState(project, group);
            if (s.status !== "complete" && (s.total > 0 || s.status === "in_progress")) {
                return group.key;
            }
        }
    }
    // Otherwise fall back to the current-phase caption.
    return currentGroupFromPhaseLabel(project);
}

/** Compute high-level lifecycle state:
 *   - "no_project"   → customer has no project (rare, honest empty)
 *   - "active"       → an installation is in progress
 *   - "commissioned" → every required task is complete
 */
export function projectLifecycle(project) {
    if (!project) return "no_project";
    const state = (project.state || "").toLowerCase();
    if (state === "completed" || state === "commissioned") return "commissioned";
    if ((project.progress ?? 0) >= 100) return "commissioned";
    return "active";
}

/** CUST-EXP-001A2 · Hero artwork decision.
 *
 * Returns one of:
 *   • "completed"    — progress ≥ 100 OR project state is completed/commissioned;
 *   • "in_progress"  — the canonical `installation` phase has any task
 *                      that is either "in_progress" or "complete", OR
 *                      the current-phase caption indicates the project
 *                      has moved beyond pre-installation (Installation,
 *                      Commissioning, Certification, Handover, Aftercare);
 *   • "pre_install"  — everything else (fresh project, missing/legacy
 *                      journey data, ambiguous state). Never implies
 *                      hardware is fitted.
 *
 * Equipment presence alone can NEVER drive the completed artwork —
 * it is derived exclusively from the authoritative project state.
 */
const IN_PROGRESS_GROUPS_AT_OR_AFTER_INSTALLATION = new Set([
    "installation", "certification", "handover", "aftercare",
]);

export function heroArtworkState(project) {
    if (!project) return "pre_install";
    if (projectLifecycle(project) === "commissioned") return "completed";
    // Task-truth first — with position recovery for phase_key.
    const resolved = _phasesWithResolvedKeys(project);
    const install = resolved.find((p) => _phaseKey(p) === "installation");
    if (install) {
        const started = (install.tasks || []).some((t) => {
            const s = (t.status || "").toLowerCase();
            return s === "in_progress" || s === "complete";
        });
        if (started) return "in_progress";
        // Phase-status fallback.
        const st = _phaseStatus(install);
        if (st === "in_progress" || st === "complete") return "in_progress";
    }
    // Current-phase caption fallback (when phases[] is absent OR
    // installation phase isn't in the returned slice).
    const grp = currentGroupFromPhaseLabel(project);
    if (grp && IN_PROGRESS_GROUPS_AT_OR_AFTER_INSTALLATION.has(grp)) return "in_progress";
    return "pre_install";
}
